import { supabase } from './supabase';
import type {
  Account,
  AccountMember,
  AccountMemberWithProfile,
  AccountWithMembers,
  AlarmSite,
  AlarmSiteWithContacts,
  DeviceDeactivationLog,
  MemberSiteGrant,
  SiteContact,
  Invoice,
} from '@asi/shared';

// ─── Accounts ────────────────────────────────────────────────

export async function fetchAccounts(): Promise<Account[]> {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .order('account_number', { ascending: true });
  if (error) throw error;
  return data as Account[];
}

export async function fetchAccountById(id: string): Promise<AccountWithMembers> {
  const { data, error } = await supabase
    .from('accounts')
    .select(`
      *,
      members:account_members!account_id(
        *,
        profile:profiles!user_id(id, full_name, email, phone, role, avatar_url)
      )
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  const result = data as unknown as AccountWithMembers;
  // Filter out soft-deleted members
  if (result.members) {
    (result as any).members = result.members.filter((m: any) => m.is_active !== false);
  }
  return result;
}

export async function createAccount(
  payload: Pick<Account, 'name' | 'type'> & Partial<Pick<Account, 'nit' | 'phone' | 'billing_email' | 'billing_name' | 'billing_address' | 'address' | 'department_id' | 'municipality_id' | 'city' | 'state' | 'monthly_fee' | 'notes'>>
): Promise<Account> {
  const { data, error } = await supabase
    .from('accounts')
    .insert({ ...payload, account_number: '' }) // trigger fills account_number
    .select()
    .single();
  if (error) throw error;
  return data as Account;
}

export async function updateAccount(
  id: string,
  payload: Partial<Pick<Account, 'name' | 'type' | 'nit' | 'phone' | 'billing_email' | 'billing_name' | 'billing_address' | 'address' | 'department_id' | 'municipality_id' | 'city' | 'state' | 'monthly_fee' | 'billing_day' | 'notes' | 'is_active' | 'is_demo' | 'patrol_radius_km' | 'client_tier'>> & { registration_source?: string }
): Promise<Account> {
  const { data, error } = await supabase
    .from('accounts')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as Account;
}

export async function setAccountActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('accounts')
    .update({ is_active: isActive })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Flip the demo flag on an account. When enabled, every active member
 * of the account is automatically flagged `profiles.is_demo=true` by a
 * Postgres trigger and the `process-alert` edge function rejects any
 * real alert they try to send. Use for prospect demos / sandboxes.
 */
export async function setAccountDemo(id: string, isDemo: boolean): Promise<void> {
  const { error } = await supabase
    .from('accounts')
    .update({ is_demo: isDemo })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Hard-delete an account via the `delete-account` edge function. The edge
 * function pre-cleans invoices / attempts / receipts before issuing the
 * final DELETE so test accounts can be purged in one click. Pass
 * `deleteMembers=true` (the "forzar" option in the UI) to also wipe
 * alarm_sites + cameras + panels owned by the account — use only for
 * QA / test data.
 */
export async function deleteAccount(
  id: string,
  options: { deleteMembers?: boolean } = {},
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('delete-account', {
    body: { accountId: id, deleteMembers: !!options.deleteMembers },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
}

// ─── Account Feature Flags ───────────────────────────────────

export type AccountFeatures = Pick<
  Account,
  'feature_cameras' | 'feature_panels' | 'feature_arc' | 'feature_patrols'
>;

export async function updateAccountFeatures(
  accountId: string,
  features: Partial<AccountFeatures>,
): Promise<void> {
  const { error } = await supabase
    .from('accounts')
    .update(features)
    .eq('id', accountId);
  if (error) throw error;
}

// ─── Deactivation log ────────────────────────────────────────

export async function fetchDeactivationLog(
  filters: { siteId?: string; accountId?: string; limit?: number } = {}
): Promise<DeviceDeactivationLog[]> {
  let query = supabase
    .from('device_deactivation_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(filters.limit ?? 100);

  if (filters.siteId) query = query.eq('site_id', filters.siteId);
  if (filters.accountId) query = query.eq('account_id', filters.accountId);

  const { data, error } = await query;
  if (error) throw error;
  return data as DeviceDeactivationLog[];
}

// ─── Account Members ─────────────────────────────────────────

export async function fetchAccountMembers(accountId: string): Promise<AccountMemberWithProfile[]> {
  const { data, error } = await supabase
    .from('account_members')
    .select('*, profile:profiles!user_id(id, full_name, email, phone, role, avatar_url)')
    .eq('account_id', accountId)
    .eq('is_active', true)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data as unknown as AccountMemberWithProfile[];
}

export async function addAccountMember(
  accountId: string,
  userId: string,
  role: 'owner' | 'admin' | 'manager' | 'worker' | 'technician' | 'familiar',
  hasAppLicense: boolean,
  hasManagedAccess: boolean,
): Promise<void> {
  // is_active: true ensures previously-deactivated members are re-enabled.
  // The DB trigger fn_member_inherit_managed sets is_managed automatically.
  // has_app_license / has_managed_access are always explicit here (never left
  // to the trigger default) so a REACTIVATION never silently inherits a stale
  // true/false value from a previous, unrelated membership stint.
  const { error } = await supabase
    .from('account_members')
    .upsert(
      {
        account_id: accountId, user_id: userId, role, is_active: true,
        has_app_license: hasAppLicense, has_managed_access: hasManagedAccess,
      },
      { onConflict: 'account_id,user_id' },
    );
  if (error) throw error;
}

export async function removeAccountMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('account_members')
    .update({ is_active: false })
    .eq('id', memberId);
  if (error) throw error;
}

export async function updateMemberRole(
  memberId: string,
  role: 'owner' | 'admin' | 'manager' | 'worker' | 'technician' | 'familiar',
): Promise<void> {
  const { error } = await supabase
    .from('account_members')
    .update({ role })
    .eq('id', memberId);
  if (error) throw error;
}

// ─── Member delegation ───────────────────────────────────────

export type MemberCapabilities = Pick<
  AccountMember,
  'can_view_cameras' | 'can_view_panels' | 'can_arm_disarm' | 'can_view_history'
>;

export async function updateMemberCapabilities(
  memberId: string,
  caps: MemberCapabilities,
): Promise<void> {
  const { error } = await supabase
    .from('account_members')
    .update(caps)
    .eq('id', memberId);
  if (error) throw error;
}

export async function fetchMemberSiteGrants(memberId: string): Promise<MemberSiteGrant[]> {
  const { data, error } = await supabase
    .from('member_site_grants')
    .select('*')
    .eq('member_id', memberId);
  if (error) throw error;
  return data as MemberSiteGrant[];
}

/**
 * Replace all site grants for a member.
 * Passing an empty array = access to ALL sites (no restrictions).
 */
export async function setMemberSiteGrants(
  memberId: string,
  siteIds: string[],
): Promise<void> {
  const { error: delError } = await supabase
    .from('member_site_grants')
    .delete()
    .eq('member_id', memberId);
  if (delError) throw delError;

  if (siteIds.length === 0) return;

  const rows = siteIds.map((site_id) => ({ member_id: memberId, site_id }));
  const { error: insError } = await supabase.from('member_site_grants').insert(rows);
  if (insError) throw insError;
}

export async function fetchMemberCameraGrants(memberId: string): Promise<{ camera_id: string }[]> {
  const { data, error } = await supabase
    .from('member_camera_grants')
    .select('camera_id')
    .eq('member_id', memberId);
  if (error) throw error;
  return (data ?? []) as { camera_id: string }[];
}

/**
 * Replace all camera grants for a member.
 * Passing an empty array = access to ALL cameras (no restrictions).
 */
export async function setMemberCameraGrants(
  memberId: string,
  cameraIds: string[],
): Promise<void> {
  const { error: delError } = await supabase
    .from('member_camera_grants')
    .delete()
    .eq('member_id', memberId);
  if (delError) throw delError;

  if (cameraIds.length === 0) return;

  const rows = cameraIds.map((camera_id) => ({ member_id: memberId, camera_id }));
  const { error: insError } = await supabase.from('member_camera_grants').insert(rows);
  if (insError) throw insError;
}

/**
 * Fetch all cameras (id + name) belonging to an account, via alarm_sites join.
 */
export async function fetchCamerasByAccount(accountId: string): Promise<{ id: string; name: string }[]> {
  // Get site IDs for this account
  const { data: siteData, error: siteError } = await supabase
    .from('alarm_sites')
    .select('id')
    .eq('account_id', accountId);
  if (siteError) throw new Error(siteError.message);

  const siteIds = (siteData ?? []).map((s: { id: string }) => s.id);
  if (siteIds.length === 0) return [];

  const { data, error } = await supabase
    .from('cameras')
    .select('id, name')
    .in('site_id', siteIds)
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: string; name: string }[];
}

/**
 * Syncs client_sites for a member user so the mobile app and RLS policies
 * reflect the same access as member_site_grants.
 *
 * Semantics match member_site_grants:
 *   grantedSiteIds = [] → access to ALL account sites
 *   grantedSiteIds = [...] → access ONLY to those sites
 *
 * Only touches rows scoped to accountSiteIds to avoid interfering with
 * assignments from other accounts.
 */
export async function syncClientSites(
  userId: string,
  accountSiteIds: string[],
  grantedSiteIds: string[],
): Promise<void> {
  if (accountSiteIds.length === 0) return;

  // Remove existing entries for this user scoped to this account's sites
  const { error: delError } = await supabase
    .from('client_sites')
    .delete()
    .eq('client_id', userId)
    .in('site_id', accountSiteIds);
  if (delError) throw delError;

  const effectiveSiteIds = grantedSiteIds.length === 0 ? accountSiteIds : grantedSiteIds;
  if (effectiveSiteIds.length === 0) return;

  const rows = effectiveSiteIds.map((site_id) => ({ client_id: userId, site_id }));
  const { error: insError } = await supabase
    .from('client_sites')
    .upsert(rows, { onConflict: 'client_id,site_id', ignoreDuplicates: true });
  if (insError) throw insError;
}

/**
 * Returns all client_sites for a user, joined with site name and account info.
 * Used to show cross-account site access in the delegation modal.
 */
export interface ClientSiteEntry {
  site_id: string;
  site_name: string;
  account_name: string | null;
  account_number: string | null;
  account_id: string | null;
}

export async function fetchAllClientSites(userId: string): Promise<ClientSiteEntry[]> {
  const { data, error } = await supabase
    .from('client_sites')
    .select(`
      site_id,
      alarm_sites!inner (
        name,
        account_id,
        accounts ( name, account_number )
      )
    `)
    .eq('client_id', userId);
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    site_id: row.site_id,
    site_name: row.alarm_sites?.name ?? row.site_id,
    account_id: row.alarm_sites?.account_id ?? null,
    account_name: row.alarm_sites?.accounts?.name ?? null,
    account_number: row.alarm_sites?.accounts?.account_number ?? null,
  }));
}

export async function removeClientSite(userId: string, siteId: string): Promise<void> {
  const { error } = await supabase
    .from('client_sites')
    .delete()
    .eq('client_id', userId)
    .eq('site_id', siteId);
  if (error) throw error;
}

// ─── Sites by account ────────────────────────────────────────

export async function createSite(
  accountId: string,
  payload: Pick<AlarmSite, 'name'> & Partial<Pick<AlarmSite, 'address' | 'city' | 'clave' | 'tipo' | 'ubicacion' | 'notes' | 'department_id' | 'municipality_id' | 'lat' | 'lng' | 'camera_mode'>>
): Promise<void> {
  const { error } = await supabase
    .from('alarm_sites')
    .insert({ ...payload, account_id: accountId });
  if (error) throw new Error(error.message);
}

export async function fetchSitesByAccount(accountId: string): Promise<AlarmSiteWithContacts[]> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select(`
      *,
      contacts:site_contacts(*)
    `)
    .eq('account_id', accountId)
    .order('name', { ascending: true });
  if (error) throw error;
  // sort contacts by call_order
  const sites = (data as unknown as AlarmSiteWithContacts[]).map((s) => ({
    ...s,
    contacts: [...(s.contacts ?? [])].sort((a, b) => a.call_order - b.call_order),
    account: null,
  }));
  return sites;
}

export async function fetchSiteById(siteId: string): Promise<AlarmSiteWithContacts> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select(`
      *,
      contacts:site_contacts(*),
      account:accounts(*)
    `)
    .eq('id', siteId)
    .single();
  if (error) throw error;
  const site = data as unknown as AlarmSiteWithContacts;
  site.contacts = [...(site.contacts ?? [])].sort((a, b) => a.call_order - b.call_order);
  return site;
}

export async function updateSite(
  siteId: string,
  payload: Partial<Pick<AlarmSiteWithContacts, 'name' | 'address' | 'city' | 'state' | 'clave' | 'tipo' | 'ubicacion' | 'notes' | 'is_active' | 'department_id' | 'municipality_id' | 'lat' | 'lng' | 'arm_reminder_enabled' | 'arm_reminder_message' | 'arm_reminder_start' | 'arm_reminder_end' | 'arm_reminder_radius_m' | 'camera_mode'>>
): Promise<void > {
  const { error } = await supabase.from('alarm_sites').update(payload).eq('id', siteId);
  if (error) throw error;
}

/** Fetch alarm_sites that have not yet been assigned to any account (account_id IS NULL). */
export async function fetchUnassignedSites(): Promise<AlarmSite[]> {
  const { data, error } = await supabase
    .from('alarm_sites')
    .select('id, name, address, city, hpp_site_id, account_id, created_at, updated_at')
    .is('account_id', null)
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AlarmSite[];
}

/** Assign an existing alarm_site to an account (sets account_id). */
export async function assignSiteToAccount(siteId: string, accountId: string): Promise<void> {
  const { error } = await supabase
    .from('alarm_sites')
    .update({ account_id: accountId })
    .eq('id', siteId);
  if (error) throw new Error(error.message);
}

/**
 * Release a site from its account (account_id → null) so the physical panel
 * becomes available again — e.g. a client cancels service and the hardware
 * gets resold/reinstalled for a different client. The site then shows up
 * under "Sin cuenta" and a fresh onboarding QR can be generated for it.
 * Does NOT touch account_members — the former owner keeps their account
 * (with its other sites, if any); deactivate it separately if it's now empty.
 */
export async function releaseSite(siteId: string): Promise<void> {
  const { error } = await supabase
    .from('alarm_sites')
    .update({ account_id: null })
    .eq('id', siteId);
  if (error) throw new Error(error.message);
}

/**
 * Fetch alarm_panels that can be linked to an account's existing site:
 * panels with no site OR panels whose current site has no account assigned.
 * Uses the same JOIN + filter logic as the AlarmsPage "Sin cuenta" tab to
 * guarantee both views agree on which panels are unowned.
 */
export async function fetchPanelsAvailableToLink(): Promise<{ id: string; name: string; serial_number: string; model: string | null; site_name: string | null }[]> {
  // Single query: fetch all panels with their site's account_id embedded.
  // Filter client-side: panel is "available" if it has no site at all, or its
  // site has no account assigned (account_id IS NULL — orphaned after account
  // deletion) or the site's account join resolves to null (dangling FK edge case).
  const { data, error } = await supabase
    .from('alarm_panels')
    .select('id, name, serial_number, model, site:alarm_sites(id, name, account_id)')
    .order('name');
  if (error) throw new Error(error.message);

  type Row = { id: string; name: string; serial_number: string; model: string | null; site: { id: string; name: string; account_id: string | null } | null };
  return ((data as unknown as Row[]) ?? [])
    .filter((p) => !p.site || !p.site.account_id)
    .map((p) => ({
      id: p.id,
      name: p.name,
      serial_number: p.serial_number,
      model: p.model,
      site_name: p.site?.name ?? null,
    }));
}

// ─── Site Contacts ────────────────────────────────────────────

export async function fetchSiteContacts(siteId: string): Promise<SiteContact[]> {
  const { data, error } = await supabase
    .from('site_contacts')
    .select('*')
    .eq('site_id', siteId)
    .eq('is_active', true)
    .order('call_order', { ascending: true });
  if (error) throw error;
  return data as SiteContact[];
}

export async function upsertSiteContact(
  contact: Partial<SiteContact> & Pick<SiteContact, 'site_id' | 'name' | 'phone' | 'call_order'>
): Promise<SiteContact> {
  const { data, error } = await supabase
    .from('site_contacts')
    .upsert(contact)
    .select()
    .single();
  if (error) throw error;
  return data as SiteContact;
}

export async function deleteSiteContact(contactId: string): Promise<void> {
  const { error } = await supabase
    .from('site_contacts')
    .update({ is_active: false })
    .eq('id', contactId);
  if (error) throw error;
}

// ─── Invoices ─────────────────────────────────────────────────

export async function fetchInvoicesByAccount(accountId: string): Promise<Invoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('account_id', accountId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data as Invoice[];
}

export interface InvoiceWithAccount extends Invoice {
  account: { id: string; name: string; account_number: string; billing_name: string | null; billing_email: string | null; billing_address: string | null; fel_nit: string | null; fel_legal_name: string | null; fel_address: string | null; fel_email_invoice: string | null; feature_cameras: boolean | null; feature_panels: boolean | null; feature_patrols: boolean | null; feature_arc: boolean | null } | null;
}

export async function fetchAllInvoices(opts?: { status?: string; limit?: number; offset?: number }): Promise<InvoiceWithAccount[]> {
  let query = supabase
    .from('invoices')
    .select('*, account:accounts(id, name, account_number, billing_name, billing_email, billing_address, fel_nit, fel_legal_name, fel_address, fel_email_invoice, feature_cameras, feature_panels, feature_patrols, feature_arc)')
    .order('created_at', { ascending: false });
  if (opts?.status) query = query.eq('status', opts.status);
  if (opts?.limit)  query = query.limit(opts.limit);
  if (opts?.offset) query = query.range(opts.offset, opts.offset + (opts.limit ?? 50) - 1);
  const { data, error } = await query;
  if (error) throw error;
  return data as InvoiceWithAccount[];
}

// ─── Geography ───────────────────────────────────────────────

export interface GeoDepartment { id: number; name: string; }
export interface GeoMunicipality { id: number; name: string; department_id: number; }

export async function fetchDepartments(): Promise<GeoDepartment[]> {
  const { data, error } = await supabase
    .from('geo_departments')
    .select('id, name')
    .order('name', { ascending: true });
  if (error) throw error;
  return data as GeoDepartment[];
}

export async function fetchMunicipalities(departmentId: number): Promise<GeoMunicipality[]> {
  const { data, error } = await supabase
    .from('geo_municipalities')
    .select('id, name, department_id')
    .eq('department_id', departmentId)
    .order('name', { ascending: true });
  if (error) throw error;
  return data as GeoMunicipality[];
}

// ─── Alert Recipients ────────────────────────────────────────
// Manage who receives FCM push notifications when a member of an account
// triggers an SOS / PANIC / FIRE / MEDICAL alert.
//   • source_user_id IS NULL  → owner-managed default for the entire account
//   • source_user_id IS NOT NULL → recipient added by that specific member
//                                  (only fires when that member is the sender)

export interface AlertRecipient {
  id: string;
  account_id: string;
  user_id: string;
  source_user_id: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // Joined:
  profile?: { id: string; full_name: string; email: string } | null;
  source_profile?: { id: string; full_name: string; email: string } | null;
}

export async function fetchAlertRecipients(accountId: string): Promise<AlertRecipient[]> {
  const { data, error } = await supabase
    .from('account_alert_recipients')
    .select(`
      *,
      profile:profiles!user_id(id, full_name, email),
      source_profile:profiles!source_user_id(id, full_name, email)
    `)
    .eq('account_id', accountId)
    .order('source_user_id', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data as unknown as AlertRecipient[];
}

export async function addAlertRecipient(
  accountId: string,
  userId: string,
  sourceUserId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('account_alert_recipients')
    .upsert(
      { account_id: accountId, user_id: userId, source_user_id: sourceUserId, enabled: true },
      { onConflict: 'account_id,user_id,source_user_id', ignoreDuplicates: false },
    );
  if (error) throw error;
}

export async function setAlertRecipientEnabled(
  recipientId: string,
  enabled: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('account_alert_recipients')
    .update({ enabled })
    .eq('id', recipientId);
  if (error) throw error;
}

export async function removeAlertRecipient(recipientId: string): Promise<void> {
  const { error } = await supabase
    .from('account_alert_recipients')
    .delete()
    .eq('id', recipientId);
  if (error) throw error;
}

